import { inject, Injectable } from '@angular/core';
import { JwtService } from '../jwt/jwt-service';

@Injectable({
  providedIn: 'root'
})
export class DateTimeService {
  private jwtService = inject(JwtService);
  /** Convert Date object → UTC string ("YYYY-MM-DD") */
  toUtcString(date: Date): string {
    const utc = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    return utc.toISOString().split('T')[0];
  }

  formatYYYYMMDD(dateStr: string | null): string {
    if (!dateStr) return '';
    return dateStr.split('T')[0]; // '2026-06-12'
  }

  formatDDMMYYYY(dateStr: string | null): string {
    if (!dateStr) return '';
    const [year, month, day] = dateStr.split('T')[0].split('-');
    return `${day}-${month}-${year}`; // '12-06-2026'
  }

  //convert date object to UTC time only string (HH:mm)
  toUtcTimeOnly(date: Date): string | null {
    if (!date) return null;

    const hours = String(date.getUTCHours()).padStart(2, '0');
    const minutes = String(date.getUTCMinutes()).padStart(2, '0');

    return `${hours}:${minutes}`;
  }

  /** UTC string */
  utcFormat(value: any) {
    if (!value) return null;

    if (value instanceof Date) {
      return this.toUtcString(value);
    }

    if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value)) {
      return this.toUtcString(new Date(value));
    }

    return null;
  }

  utcFormatMultiple(obj: any, dateFields: string[]) {
    if (!obj) return obj;

    const result = { ...obj };
    dateFields.forEach((field) => {
      if (result[field] !== undefined && result[field] !== null) {
        result[field] = this.utcFormat(result[field]);
      }
    });
    return result;
  }

  toDateObject(value: any, dateFields?: string[]): any {
    // If dateFields is provided, treat value as an object with multiple dates
    if (dateFields && Array.isArray(dateFields)) {
      if (!value) return value;

      const result = { ...value };
      dateFields.forEach((field) => {
        if (result[field] !== undefined && result[field] !== null) {
          result[field] = this.convertToDateObject(result[field]);
        }
      });
      return result;
    }

    // Otherwise, convert a single date value
    return this.convertToDateObject(value);
  }

  convertToDateObject(date: string | Date | null): Date | null {
    if (!date) return null;

    if (date instanceof Date) return isNaN(date.getTime()) ? null : date;

    if (typeof date === 'string') {
      // remove Z from date string, convert to date with no utc offset
      if (date.toLowerCase().endsWith('z')) {
        date = date.slice(0, -1);
      }
      const parsed = new Date(date);
      return isNaN(parsed.getTime()) ? null : parsed;
    }

    return null;
  }

  getFormattedDate(): string {
    const now = new Date();
    const options: Intl.DateTimeFormatOptions = {
      weekday: 'short',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
      timeZoneName: 'short'
    };

    return now.toLocaleString('en-GB', options).replace(',', '').replace('GMT', 'UTC');
  }

  timeFormat(value: any, format: string = 'HH:mm'): string | null {
    if (!value) return null;

    const date = new Date(value);
    if (isNaN(date.getTime())) return null; // Invalid date safety check

    let hours = date.getHours();
    let minutes = String(date.getMinutes()).padStart(2, '0');
    let seconds = String(date.getSeconds()).padStart(2, '0');

    const ampm = hours >= 12 ? 'PM' : 'AM';
    const hours12 = hours % 12 || 12;
    const hours24 = String(hours).padStart(2, '0');
    const hours12Str = String(hours12).padStart(2, '0');

    // Handle format tokens
    return format.replace(/HH/g, hours24).replace(/hh/g, hours12Str).replace(/mm/g, minutes).replace(/ss/g, seconds).replace(/A/g, ampm);
  }

  // String to Timepicker
  toTimeObject(timeString: string | null): Date | null {
    if (!timeString) return null;

    const [hours, minutes] = timeString.split(':').map(Number);
    const time = new Date();
    time.setHours(hours);
    time.setMinutes(minutes);
    time.setSeconds(0);
    time.setMilliseconds(0);

    return time;
  }

  // Combine date + time and change to UTC time
  convertToUtcWithJwtOffset(date: Date | null, time?: Date | null, end: boolean = false): string | null {
    if (!date) return null;
    const match = this.jwtService.getTimezoneUtc().match(/UTC\s*([+-])(\d{2}):(\d{2})/);
    if (!match) return null;
    const sign = match[1] === '+' ? 1 : -1;
    const offsetHours = Number(match[2]);
    const offsetMinutes = Number(match[3]);
    const totalOffsetMinutes = sign * (offsetHours * 60 + offsetMinutes);
    const year = date.getFullYear();
    const month = date.getMonth();
    const day = date.getDate();
    const hours = time ? time.getHours() : date.getHours();
    const minutes = time ? time.getMinutes() : date.getMinutes();
    let seconds, ms;
    if (end) {
      seconds = 59;
      ms = 999;
    } else {
      seconds = 0;
      ms = 0;
    }
    const utcDate = new Date(Date.UTC(year, month, day, hours, minutes, seconds, ms));
    utcDate.setUTCMinutes(utcDate.getUTCMinutes() - totalOffsetMinutes);
    return utcDate.toISOString();
  }

  //Reverse the timezone offset and convert to a Date & Time separately.
  convertFromUtcWithJwtOffset(value: string | null): Date | null {
    if (!value) return null;

    const utcDate = new Date(value);
    if (Number.isNaN(utcDate.getTime())) return null;

    const match = this.jwtService.getTimezoneUtc().match(/UTC\s*([+-])(\d{2}):(\d{2})/);

    if (!match) return null;

    const sign = match[1] === '+' ? 1 : -1;
    const offsetMinutes = sign * (Number(match[2]) * 60 + Number(match[3]));

    const shiftedDate = new Date(utcDate.getTime() + offsetMinutes * 60_000);

    // Build a Date whose displayed fields match the JWT timezone.
    return new Date(
      shiftedDate.getUTCFullYear(),
      shiftedDate.getUTCMonth(),
      shiftedDate.getUTCDate(),
      shiftedDate.getUTCHours(),
      shiftedDate.getUTCMinutes(),
      shiftedDate.getUTCSeconds(),
      shiftedDate.getUTCMilliseconds()
    );
  }

  // Change to local time
  convertToLocalDate(dateString: string | null): Date | null {
    if (!dateString) return null;
    const utcDate = new Date(dateString);
    const match = this.jwtService.getTimezoneUtc().match(/UTC\s*([+-])(\d{2}):(\d{2})/);
    if (!match) return null;
    const sign = match[1] === '+' ? 1 : -1;
    const offsetHours = Number(match[2]);
    const offsetMinutes = Number(match[3]);
    const totalOffsetMinutes = sign * (offsetHours * 60 + offsetMinutes);
    utcDate.setUTCMinutes(utcDate.getUTCMinutes() + totalOffsetMinutes);
    return utcDate;
  }

  //Change UTC format to a date time string
  formatDisplayDateTime(value: string | Date | null): string | null {
    const date = this.convertToDateObject(value);
    if (!date) return null;
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    const time = this.timeFormat(date, 'hh:mm A');
    return `${day}-${month}-${year} ${time}`;
  }

  // Bulk changes verion of formatDisplayDateTime
  formatDisplayDateTimeMultiple(obj: any, dateFields: string[]): any {
    if (!obj) return obj;
    const result = { ...obj };
    dateFields.forEach((field) => {
      if (result[field] !== undefined && result[field] !== null) {
        result[field] = this.formatDisplayDateTime(result[field]);
      }
    });
    return result;
  }
}
